import React, { useEffect, useRef } from 'react';
import useWidgetStream from 'widgets/streaming/useWidgetStream.ts';
import {
    useDarkVenueHeatMapData,
    useReRunDarkVenueHeatMapRequest,
} from 'widgets/darkVenueHeatmap/store/hooks.ts';
import { useWidgetContext } from 'widgets/WidgetContext.tsx';
import {
    useWidgetHasValidSelection,
    useWidgetPortfolioSelectionStatus,
} from 'widgets/hooks.ts';
import './DarkVenueHeatmap.scss';
import { WarningPanel } from 'utils/components/WarningPanel/WarningPanel';
import { DarkVenueHeatmapSymbolDataState } from 'widgets/darkVenueHeatmap/store/data/DarkVenueHeatmapDataState';
import DarkVenueHeatmapChart from 'widgets/darkVenueHeatmap/DarkVenueHeatmapChart';
import { isOkResponse } from 'widgets/darkVenueHeatmap/api/DarkVenueHeatmapApiResponse';
import { useDarkVenueHeatmapWidgetConfigSlice } from 'widgets/darkVenueHeatmap/store/config/configslice';
import styleConstants from 'styles/variables.module.scss';
import DarkVenueHeatmapToolbar from 'widgets/darkVenueHeatmap/DarkVenueHeatmapToolbar';
import useDarkVenueMappings from 'widgets/darkVenueHeatmap/venueMappings/useVenueMappings.ts';
import { VenueMappingsResponse } from 'widgets/darkVenueHeatmap/venueMappings/types.ts';

const padding = 2 * parseInt(styleConstants.spaceAround);
const toolbarHeight = parseInt(styleConstants.panelHeaderHeight);
const borderWidth = 2 * parseInt(styleConstants.layoutBorderWidth);

const RefreshIntervalMins = 15;

export default function DarkVenueHeatmap() {
    const { widgetKey, height } = useWidgetContext();
    const hasValidSelection = useWidgetHasValidSelection();
    const mappings = useDarkVenueMappings();

    useWidgetStream();

    const { config } = useDarkVenueHeatmapWidgetConfigSlice();

    const hideToolbar = !!config?.hideToolbar || !hasValidSelection;

    const toolbarConsumedHeight = hideToolbar ? 0 : toolbarHeight;
    const bodyHeight = hideToolbar
        ? height - borderWidth - padding
        : height - toolbarConsumedHeight - borderWidth - padding;

    const data = useDarkVenueHeatMapData();
    const reRun = useReRunDarkVenueHeatMapRequest();

    const intervalRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        intervalRef.current = setInterval(reRun, RefreshIntervalMins * 60000);
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [reRun]);

    return (
        <div className="DarkVenueHeatmap" data-widget-id={widgetKey}>
            <DarkVenueHeatmapToolbar />

            <div className="widget-body" style={{ height: bodyHeight }}>
                <WidgetBody
                    data={data}
                    height={bodyHeight}
                    mappings={mappings}
                />
            </div>
        </div>
    );
}

export function WidgetBody({
    data,
    height,
    mappings,
}: {
    data: DarkVenueHeatmapSymbolDataState | undefined;
    height: number;
    mappings: VenueMappingsResponse;
}) {
    const { hasValidSelection } = useWidgetPortfolioSelectionStatus();
    if (!hasValidSelection) {
        return <></>;
    }

    return (
        <WidgetContent
            data={data}
            hasValidSelection={hasValidSelection}
            height={height}
            mappings={mappings}
        />
    );
}

function WidgetContent({
    data,
    hasValidSelection,
    height,
    mappings,
}: {
    data: DarkVenueHeatmapSymbolDataState | undefined;
    hasValidSelection: boolean;
    height: number;
    mappings: VenueMappingsResponse;
}) {
    if (!hasValidSelection) {
        return <></>;
    }

    if (data?.status === 'Completed' && isOkResponse(data.response)) {
        return (
            <DarkVenueHeatmapChart
                data={data.response!}
                height={height}
                mappings={mappings}
            />
        );
    }
    if (data?.status === 'Failed') {
        return <ErrorPanel data={data} />;
    }
    if (data?.status === 'In Progress') {
        return <InProgressPanel />;
    }
    return <></>;
}

function InProgressPanel() {
    return (
        <div className="InProgressPanel info-box">
            <div className="ui active inline loader mini" />
            <span className="message">Fetching data...</span>
        </div>
    );
}

function ErrorPanel({ data }: { data: DarkVenueHeatmapSymbolDataState }) {
    const message = extractErrorMessage(data);
    return (
        <div className="ErrorPanel">
            <WarningPanel text={message} />
        </div>
    );
}

export function extractErrorMessage(data?: DarkVenueHeatmapSymbolDataState) {
    const error = data?.error as { message?: string };
    return (
        'Query Failed' +
        (error?.message
            ? ` - ${error.message.replace('Dark Venue Heatmap failed: ', '')}`
            : '')
    );
}
